import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import { Modal } from 'react-bootstrap'
import axios from '../axiosInstance'
import Sidebar from '../components/sidebar'
import Topbar from '../components/topbar'
import Pagination from '../components/Pagination'
import { errorMessage } from '../hooks/UserLogs'
import { convertToDate } from '../utils/date'
import './SystemLogs.css'

const PAGE_LIMIT = 10

const getDefaultDateRange = () => {
  const begin = new Date()
  begin.setMonth(begin.getMonth() - 3)
  const end = new Date()
  end.setDate(end.getDate() + 1)
  return { begin, end }
}

export default function SystemLogs () {
  const lng = useSelector((state) => state)
  const navigate = useNavigate()
  const bUrl = process.env.REACT_APP_B_URL

  const { begin: defaultBegin, end: defaultEnd } = getDefaultDateRange()

  const [beginDate, setBeginDate] = useState(defaultBegin)
  const [endDate, setEndDate] = useState(defaultEnd)
  const [logFiles, setLogFiles] = useState([])
  const [totalPage, setTotalPage] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [downloadingName, setDownloadingName] = useState(null)
  const [showError, setShowError] = useState(false)
  const [errMsg, setErrMsg] = useState('')

  const axiosInstance = axios.create({ withCredentials: true })

  const formatDisplayDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  const fetchLogFiles = useCallback(
    async (bgDate, edDate, page) => {
      setIsLoading(true)
      const bgDateConvert = convertToDate(new Date(bgDate))
      const endDateConvert = convertToDate(new Date(edDate))

      const response = await axiosInstance
        .post(
          bUrl + '/logs',
          {
            beginDate: bgDateConvert,
            endDate: endDateConvert,
            page,
            limit: PAGE_LIMIT,
          },
          { headers: { 'Content-Type': 'application/json' } }
        )
        .catch((err) => {
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            setErrMsg(lng.UnauthorizedOperation || "Vous n'êtes pas autorisé à faire cette opération.")
            setShowError(true)
            if (err?.response?.status === 401) {
              navigate('/backoffice/login')
            }
          } else {
            setErrMsg(lng.GenericError || "Une erreur s'est produite.")
            setShowError(true)
          }
          setIsLoading(false)
          return null
        })

      if (!response) return

      if (response.status === 401) {
        navigate('/backoffice/login')
        return
      }

      if (response?.data?.isError) {
        const msg = errorMessage(response?.data?.code) || response?.data?.msg || lng.GenericError
        setErrMsg(msg)
        setShowError(true)
        setLogFiles([])
      } else {
        setLogFiles(response.data.body || [])
        setTotalPage(response.data.totalPage || 1)
        setCurrentPage(response.data.currentPage || page)
      }
      setIsLoading(false)
    },
    [bUrl, lng.GenericError, lng.UnauthorizedOperation, navigate]
  )

  const handleSearch = () => {
    if (currentPage !== 1) {
      setCurrentPage(1)
    } else {
      fetchLogFiles(beginDate, endDate, 1)
    }
  }

  const handleBeginDateChange = (value) => {
    setBeginDate(new Date(value))
    setCurrentPage(1)
  }

  const handleEndDateChange = (value) => {
    setEndDate(new Date(value))
    setCurrentPage(1)
  }

  const handleResetRange = () => {
    const { begin, end } = getDefaultDateRange()
    setBeginDate(begin)
    setEndDate(end)
    setCurrentPage(1)
  }

  const downloadLogFile = async (file) => {
    setDownloadingName(file.fileName)
    try {
      const response = await axiosInstance.post(
        bUrl + '/logs/download',
        { fileName: file.fileName, filePath: file.filePath },
        { responseType: 'blob' }
      )

      const blob = response.data
      if (blob?.type?.includes('application/json')) {
        const text = await blob.text()
        let parsed
        try {
          parsed = JSON.parse(text)
        } catch {
          parsed = null
        }
        if (parsed?.isError) {
          const msg = errorMessage(parsed?.code) || parsed?.msg || lng.DownloadFailed
          setErrMsg(msg)
          setShowError(true)
          return
        }
      }

      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.download = file.fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/backoffice/login')
        return
      }
      setErrMsg(lng.DownloadFailed || "Le téléchargement a échoué.")
      setShowError(true)
    } finally {
      setDownloadingName(null)
    }
  }

  useEffect(() => {
    if (sessionStorage.getItem('name') === '' || sessionStorage.getItem('name') === null) {
      navigate('/backoffice/login')
      return
    }
    fetchLogFiles(beginDate, endDate, currentPage)
  }, [beginDate, endDate, currentPage, fetchLogFiles, navigate])

  return (
    <div className="App list-page system-logs-page">
      <Sidebar />
      <section className="home">
        <div className="row">
          <div className="d-grid col-12 gap-3">
            <div className="table-content mt-2 p-2 text d-flex justify-content-between align-items-start">
              <div className="list-page-header">
                <div className="fw-bold">{lng.SystemLogFiles}</div>
              </div>
              <Topbar />
            </div>

            <div className="d-grid pb-2 gap-3">
              <div className="list-filter-card">
                <div className="list-filter-toolbar">
                  <div className="list-filter-fields">
                    <div className="list-field">
                      <label htmlFor="sl-begin">{lng.Begin}</label>
                      <input
                        id="sl-begin"
                        type="date"
                        value={beginDate.toLocaleDateString('en-CA')}
                        onChange={(e) => handleBeginDateChange(e.target.value)}
                      />
                    </div>
                    <div className="list-field">
                      <label htmlFor="sl-end">{lng.End}</label>
                      <input
                        id="sl-end"
                        type="date"
                        value={endDate.toLocaleDateString('en-CA')}
                        onChange={(e) => handleEndDateChange(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="list-actions-group" role="group" aria-label={lng.SearchLogs}>
                    <button type="button" className="list-btn-primary" onClick={handleSearch} disabled={isLoading}>
                      <i className="bx bx-search" />
                      {lng.SearchLogs}
                    </button>
                    <button type="button" className="list-btn-ghost" onClick={handleResetRange} disabled={isLoading}>
                      {lng.ResetFilters}
                    </button>
                  </div>
                </div>
              </div>

              <div className="list-table-wrap p-3">
                {isLoading ? (
                  <div className="loading">
                    <img src="../Custom.svg" alt="" />
                  </div>
                ) : logFiles.length > 0 ? (
                  <>
                    <div className="table-responsive">
                      <table className="table list-table text mb-0">
                        <thead>
                          <tr>
                            <th scope="col">#</th>
                            <th scope="col">{lng.FileName}</th>
                            <th scope="col">{lng.FilePath}</th>
                            <th scope="col">{lng.FileDate}</th>
                            <th scope="col" className="text-end">
                              {lng.Download}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {logFiles.map((file, index) => (
                            <tr key={`${file.filePath}-${index}`}>
                              <td>{(currentPage - 1) * PAGE_LIMIT + index + 1}</td>
                              <td>
                                <span>{file.fileName}</span>
                                {file.fileType ? (
                                  <div className="sl-file-path">{file.fileType}</div>
                                ) : null}
                              </td>
                              <td>
                                <span className="sl-file-path">{file.filePath}</span>
                              </td>
                              <td>{formatDisplayDate(file.fileDate)}</td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="list-table-action-btn"
                                  onClick={() => downloadLogFile(file)}
                                  disabled={downloadingName === file.fileName}
                                  title={lng.DownloadLogFile}
                                >
                                  <i className="bx bx-download" />
                                  {downloadingName === file.fileName ? lng.Downloading : lng.Download}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="table-pagination">
                      <Pagination
                        totalPage={totalPage}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                      />
                    </div>

                  </>
                ) : (
                  <div className="list-empty">
                    <i className="bx bx-folder-open" />
                    <p>{lng.NoLogFiles}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Modal show={showError} onHide={() => setShowError(false)}>
        <Modal.Header closeButton className="bg-danger">
          <Modal.Title>{lng.ErrorTitle || 'ERROR'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="text-danger text-center">{errMsg}</h5>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>
            {lng.Close}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
